import { Tab, Tooltip } from "@blueprintjs/core"
import { dateHelpers } from "@runn/calculations"
import cc from "classcat"
import React, { useRef, useState } from "react"
import { useEffect } from "react"
import { useFormContext } from "react-hook-form"
import { graphql, useFragment } from "react-relay"

import styles from "./ProjectBudgetForm.module.css"

import { ProjectBudgetForm_project$key } from "~/forms/ProjectBudgetForm/__generated__/ProjectBudgetForm_project.graphql"
import { ProjectBudgetForm_user$key } from "~/forms/ProjectBudgetForm/__generated__/ProjectBudgetForm_user.graphql"

import currency, { formatCurrency } from "~/helpers/CurrencyHelper"
import { track } from "~/helpers/analytics"
import {
  OTHER_EXPENSES_ROLE,
  OtherCostWithCalculations,
  getAllocationMessage,
} from "~/helpers/otherCosts"
import { isBillableProject } from "~/helpers/project-helpers"

import ProjectClientIcon from "~/common/ProjectClientIcon"
import Tabs from "~/common/Tabs"
import Button from "~/common/buttons/Button"

import { projectBudgetUpdateV2Relay } from "~/mutations/ProjectBudget"

import { Card } from "~/dashboard/Card"
import PhaseEditor from "~/forms/ProjectBudgetForm/Phase/PhaseEditor"
import ProjectRatesEditor from "~/forms/ProjectBudgetForm/ProjectRates/ProjectRatesEditor"
import { getSettings, setSetting } from "~/localsettings"

import BudgetEditor from "./Budget/BudgetEditor"
import BudgetSummary from "./Budget/BudgetSummary"
import NonBillableWarning from "./NonBillableWarning"
import OtherExpensesEditor from "./OtherExpenses/OtherExpensesEditor"
import {
  ProjectBudgetFormProvider,
  useProjectBudgetFormContext,
} from "./ProjectBudgetFormProvider"
import { ProjectBudgetFormData } from "./formTypes"

type Props = {
  project: ProjectBudgetForm_project$key
  user: ProjectBudgetForm_user$key
  onClose: () => void
}

type BudgetTabID = "expenses" | "project-rates" | "budget"

export type RateCardOption = { value: number; label: string }

const userFragment = graphql`
  fragment ProjectBudgetForm_user on users {
    id
    permissions
    account {
      id
      default_full_time_minutes
      roles {
        id
        name
        active
      }
      rate_cards {
        id
        name
        blended_rate: blended_rate_private
        blended_rate_card
        role_charge_out_rates {
          role_id
          rate_card_id
          charge_out_rate: charge_out_rate_private
        }
      }
    }
  }
`

const projectFragment = graphql`
  fragment ProjectBudgetForm_project on projects {
    id
    name
    active
    pricing_model
    rate_type
    total_budget: total_budget_private
    no_phase_revenue_budget: no_phase_revenue_budget_private
    expenses_budget: expenses_budget_private
    emoji
    is_template
    budget_method
    project_rates {
      id
      projectId: project_id
      roleId: role_id
      rate: rate_private
    }
    rate_card {
      id
      name
      blended_rate: blended_rate_private
      blended_rate_card
      role_charge_out_rates {
        role_id
        rate_card_id
        charge_out_rate: charge_out_rate_private
      }
    }
    project_roles {
      id
      role_id
      estimated_minutes
    }
    members {
      id
      role_id
    }
    client {
      internal
      id
      image_key
      website
    }
    other_costs(order_by: { date: asc }) {
      id
      name
      date: date_runn
      cost
      charge
      phaseId: phase_id
    }
    phases {
      id
      revenue_budget: revenue_budget_private
    }

    ...PhaseEditor_project
    ...OtherExpensesEditor_project
  }
`

// Main form component that uses form context
const ProjectBudgetFormContent = (props: Props) => {
  const { onClose } = props
  const viewer = useFragment<ProjectBudgetForm_user$key>(
    userFragment,
    props.user,
  )
  const project = useFragment<ProjectBudgetForm_project$key>(
    projectFragment,
    props.project,
  )
  const {
    account,
    account: { default_full_time_minutes },
  } = viewer

  // Use the form context to get all form state and calculations
  const {
    totalExpenses,
    budgetAllocationDifference,
    expensesAllocationDifference,
    hideFinancials,
    getMutationInput,
    extractedRoles,
    showNonBillableView,
  } = useProjectBudgetFormContext()

  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()

  // Keep only non-form state
  const [isSaving, setIsSaving] = useState(false)

  const isBillable = isBillableProject(
    project.pricing_model,
    project.client.internal,
  )
  const updatingToNonBillable = isBillable && formData.pricingModel === "nb"

  const rateCards: RateCardOption[] = account.rate_cards.map((rateCard) => ({
    value: rateCard.id,
    label: rateCard.name,
  }))

  const selectedRateCard = {
    value: formData.rateCardId,
    label:
      account.rate_cards.find((rc) => rc.id === formData.rateCardId)?.name ||
      "",
  }

  // Tab state management (separate from form)
  let defaultTabId = getSettings().projectBudgetTabId ?? "budget"
  // Edge case where an unpermitted tab was persisted
  if (defaultTabId === "expenses" && hideFinancials) {
    defaultTabId = "budget"
  }

  const [budgetTabId, setBudgetTabId] = useState<BudgetTabID>(defaultTabId)

  useEffect(() => {
    track("Tab Changed", { type: "Project Budget", tab: budgetTabId })
    if (budgetTabId === "expenses" || budgetTabId === "budget") {
      setSetting("projectBudgetTabId", budgetTabId)
    }
  }, [budgetTabId])

  // Form validation state (separate from form data)
  const [itemNameRequiredError, setItemNameRequiredError] = useState(false)

  // Update form validation when expenses change
  useEffect(() => {
    const hasError = formData.otherExpenses.some(
      (expense) =>
        // Error if: no name BUT has a cost or charge
        (!expense.name || expense.name.trim() === "") &&
        (expense.charge !== 0 || expense.cost !== 0),
    )
    setItemNameRequiredError(hasError)
  }, [formData.otherExpenses])

  // Calculate unallocated roles from form data
  const unallocatedRoles = account.roles
    .filter((r) => r.active && !formData.roles.map((x) => x.id).includes(r.id))
    .map((r) => {
      const existingBudgetedRole = project.project_roles.find(
        (pr) => r.id === pr.role_id,
      )?.id

      return {
        ...r,
        estimatedMinutes: 0,
        projectRoleId: existingBudgetedRole,
      }
    })

  const isCompleteExpense = (expense: OtherCostWithCalculations) =>
    expense.name.trim() || expense.charge !== 0 || expense.cost !== 0

  if (
    !extractedRoles.expenses &&
    !formData.otherExpenses.filter(isCompleteExpense).length &&
    !showNonBillableView
  ) {
    unallocatedRoles.push({
      ...OTHER_EXPENSES_ROLE,
      estimatedMinutes: 0,
      projectRoleId: OTHER_EXPENSES_ROLE.id,
    })
  }
  const expensesBudget = extractedRoles.expenses?.estimatedMinutes ?? null

  const isExpensesTab = budgetTabId === "expenses"
  const allocationDifference = isExpensesTab
    ? expensesAllocationDifference
    : budgetAllocationDifference

  // Determines whether to show the budget difference in the footer
  // For expenses tab: Show if there's an expense budget AND actual expenses exist AND budget method is ROLE
  // For budget tab: Show if there's any total budget set
  const showDifference = isExpensesTab
    ? !!expensesBudget &&
      totalExpenses.charge > 0 &&
      formData.budgetMethod === "ROLE"
    : budgetTabId === "budget" && !!formData.totalBudget

  const overAllocated = allocationDifference < 0

  const rateCard = account.rate_cards.find(
    (rc) => rc.id === selectedRateCard.value,
  )

  // Form update handlers using form methods

  const handleShowAllRoles = () => {
    setValue("roles", [...formData.roles, ...unallocatedRoles])
  }

  const handleAddRole = (roleId: number) => {
    const selectedRole = unallocatedRoles.find((r) => r.id === roleId)
    setValue("roles", [...formData.roles, selectedRole])
  }

  const saveBudget = async () => {
    setIsSaving(true)

    // Use the form's mutation input transformation
    const mutationInput = getMutationInput()

    await projectBudgetUpdateV2Relay({
      input: mutationInput,
      todaysDate: dateHelpers.getTodaysDate(),
    })

    track("Project Budget Updated", {
      pricing_model: formData.pricingModel,
      rate_type: formData.rateType,
      role_count: formData.roles.length,
      other_cost_count: formData.otherExpenses.length,
    })
    setIsSaving(false)
    onClose()
  }

  const tableFooterRef = useRef(null)
  const footerButtonsRef = useRef(null)

  const footerHeight =
    tableFooterRef.current?.clientHeight +
    footerButtonsRef.current?.clientHeight

  const Summary = (
    <>
      <BudgetSummary
        currency={currency}
        allowBillingMethodEdit={!project.client.internal}
      />
      {updatingToNonBillable && <NonBillableWarning />}
    </>
  )

  const BudgetPanel = (
    <>
      {!hideFinancials && Summary}
      {formData.budgetMethod === "PHASE" ? (
        <PhaseEditor project={project} />
      ) : (
        <BudgetEditor
          rateCard={rateCard}
          defaultFullTimeMinutes={default_full_time_minutes}
          unallocatedRoles={unallocatedRoles}
          onAddRole={handleAddRole}
          onShowAllRoles={handleShowAllRoles}
        />
      )}
    </>
  )

  const ProjectRatesPanel = (
    <ProjectRatesEditor
      rateCard={rateCard}
      rateCards={rateCards}
      defaultFullTimeMinutes={default_full_time_minutes}
      unallocatedRoles={unallocatedRoles}
      onAddRole={handleAddRole}
      onShowAllRoles={handleShowAllRoles}
    />
  )

  const ExpensesPanel = <OtherExpensesEditor project={project} />

  return (
    <Card style={{ width: "900px" }}>
      <div
        className={`
          ${styles.budgetContainer}
          ${hideFinancials || showNonBillableView ? styles.hideFinancials : ""}
        `}
        style={{
          maxHeight: `calc(100% - ${footerHeight}px)`,
          minHeight: "200px",
        }}
        data-test="project-budget"
      >
        <div className={styles.client}>
          <ProjectClientIcon project={project} client={project.client} border />
          <span>{project.name}</span>
        </div>
        <Tabs
          defaultSelectedTabId={budgetTabId}
          large
          id="panelBudget"
          onChange={(tabId: BudgetTabID) => setBudgetTabId(tabId)}
          rightTabs
        >
          <Tab
            id="budget"
            title="Budget"
            panel={BudgetPanel}
            style={{ paddingBottom: "18px" }}
          />
          {!showNonBillableView && !hideFinancials && (
            <Tab
              id="project-rates"
              title="Project Rates"
              panel={ProjectRatesPanel}
              style={{ paddingBottom: "18px" }}
            />
          )}
          {!hideFinancials && (
            <Tab
              id="expenses"
              title="Other Expenses"
              panel={ExpensesPanel}
              style={{ paddingBottom: "18px" }}
            />
          )}
        </Tabs>
      </div>

      <div className={styles.footerButtons} ref={footerButtonsRef}>
        {!showNonBillableView && showDifference && (
          <div
            data-test="budget-difference"
            className={cc([
              styles.footerMessage,
              {
                [styles.over]: overAllocated,
              },
            ])}
          >
            {getAllocationMessage(allocationDifference)}
            {allocationDifference !== 0 && (
              <span>{formatCurrency(Math.abs(allocationDifference))}</span>
            )}
          </div>
        )}
        <Button
          text="Cancel"
          data-test="cancel-budget-button"
          onClick={onClose}
          style={{ marginLeft: "auto" }}
        />
        <Tooltip
          content="Other expense must have an item name"
          autoFocus={false}
          disabled={!itemNameRequiredError}
        >
          <Button
            data-test="save-budget-button"
            loading={isSaving}
            text="Save"
            outlined={false}
            onClick={saveBudget}
            disabled={
              isSaving ||
              (formData.totalBudget && Number(formData.totalBudget) < 0) ||
              itemNameRequiredError
            }
          />
        </Tooltip>
      </div>
    </Card>
  )
}

// Wrapper component that provides form context
const ProjectBudgetFormWithProvider = (props: Props) => {
  const viewer = useFragment<ProjectBudgetForm_user$key>(
    userFragment,
    props.user,
  )
  const project = useFragment<ProjectBudgetForm_project$key>(
    projectFragment,
    props.project,
  )

  return (
    <ProjectBudgetFormProvider project={project} user={viewer}>
      <ProjectBudgetFormContent {...props} />
    </ProjectBudgetFormProvider>
  )
}

export default ProjectBudgetFormWithProvider
